'use client';

import type { ReactElement } from 'react';
import { useContext, useEffect, useState } from 'react';
import { type NodeProps, type Node } from '@xyflow/react';
import { GraphNodeFrame } from '../Cmarkdowngraphcontrols';
import type { FlowchartNodeData } from './flowchart-types';
import { FlowchartHoverContext } from './flowchart-context';
import { FlowchartNodeContent } from './FlowchartNodeContent';
import { FlowchartNodeHandles } from './FlowchartNodeHandles';
import { resolveFlowchartNodeViewModel } from './flowchart-node-view-model';

type FlowchartCardFlowNode = Node<FlowchartNodeData, 'flowchartCard'>;

const CURSOR_POINTER = 'pointer';
const CONTENT_PADDING = '0 16px';

export function FlowchartNode({
  data,
  sourcePosition,
  targetPosition,
}: NodeProps<FlowchartCardFlowNode>): ReactElement {
  const hoverState = useContext(FlowchartHoverContext);
  const [didEnter, setDidEnter] = useState(false);

  // Effects only run client-side, so one rAF after mount drives the entrance
  // ease-in (opacity 0 → 1, scale 0.95 → 1) without a synchronous setState.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setDidEnter(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const model = resolveFlowchartNodeViewModel({ data, didEnter, hoverState });

  return (
    <GraphNodeFrame
      shape={model.shape}
      accentColor={model.accent}
      fill={model.fill}
      borderWidth={model.borderWidth}
      isSelected={model.isSelected}
      boxShadow={model.boxShadow}
      opacity={model.opacity}
      transform={model.transform}
      transition={model.transition}
      cursor={CURSOR_POINTER}
      contentPadding={CONTENT_PADDING}
      width={data.nodeWidth}
      height={data.nodeHeight}
      handles={<FlowchartNodeHandles sourcePosition={sourcePosition} targetPosition={targetPosition} />}
    >
      <FlowchartNodeContent
        accent={model.accent}
        icon={data.icon}
        iconColor={model.iconColor}
        isUnresolved={model.isUnresolved}
        label={data.label}
        labelOrigin={data.labelOrigin}
        textColor={model.textColor}
      />
    </GraphNodeFrame>
  );
}
